/**
 * Departure-event endpoints (camera person-episode + scan-miss
 * reconciler -> "who took what"). All roles can read; server
 * scopes by SCU access via vw_user_scu_access. Vendor sessions
 * additionally see only EPC lines for their catalog items.
 */
import { apiFetch } from "./apiClient";

export type DepartureStatus = "CONFIRMED" | "PARTIAL" | "VOID";
export type DepartureLineStatus = "CONFIRMED" | "VOID";
export type DepartureConfidence = "HIGH" | "MEDIUM" | "LOW";

export interface DepartureTicketRow {
  departure_ticket_id: number;
  scu_name: string;
  /** Long-lived smartbox identifier (e.g. "SB001"). Resolves via
   *  gen5_computer -> md_smartbox; survives Pi swaps because each
   *  scu_name keeps its row in gen5_computer pointing at the same
   *  md_smartbox.id. Null if the SCU has never been associated. */
  smartbox_code: string | null;
  work_center: string | null;
  camera_person_episode_id: number;
  episode_start_utc: string | null;
  episode_end_utc: string | null;
  photo_count: number | null;
  person_photo_count: number | null;
  status: DepartureStatus;
  epc_total: number;
  epc_confirmed: number;
  date_created: string | null;
  date_updated: string | null;
}

export interface DepartureEpcLine {
  departure_ticket_epc_id: number;
  epc: string;
  line_status: DepartureLineStatus;
  pre_read_count: number | null;
  post_miss_count: number | null;
  confidence: DepartureConfidence | null;
  confirmed_event_id: number | null;
  void_reason: string | null;
  last_read_utc: string | null;
  confirmed_at: string | null;
  voided_at: string | null;
  date_created: string | null;
  date_updated: string | null;
}

export interface DeparturesListResponse {
  data: DepartureTicketRow[];
  pagination: { total: number; limit: number; offset: number };
}

export interface DepartureDetailResponse {
  ticket: Omit<DepartureTicketRow, "epc_total" | "epc_confirmed">;
  epc_lines: DepartureEpcLine[];
}

export interface DeparturesQuery {
  scu?: string;
  since?: string;
  until?: string;
  status?: DepartureStatus[];
  limit?: number;
  offset?: number;
}

async function req<T>(token: string, path: string): Promise<T> {
  return apiFetch<T>(path, token);
}

export function listDepartures(
  token: string, q: DeparturesQuery = {},
): Promise<DeparturesListResponse> {
  const params = new URLSearchParams();
  if (q.scu) params.set("scu", q.scu);
  if (q.since) params.set("since", q.since);
  if (q.until) params.set("until", q.until);
  if (q.status) q.status.forEach((s) => params.append("status", s));
  if (q.limit != null) params.set("limit", String(q.limit));
  if (q.offset != null) params.set("offset", String(q.offset));
  const qs = params.toString() ? `?${params.toString()}` : "";
  return req(token, `/api/departures${qs}`);
}

export function fetchDeparture(
  token: string, id: number,
): Promise<DepartureDetailResponse> {
  return req(token, `/api/departures/${id}`);
}

/** Flat list of CONFIRMED EPC lines (the actual tags taken). The
 *  primary view on the SPA Departures page — operators want to see
 *  transactions per EPC, not one row per camera-episode. */
export interface DepartureEpcRow {
  departure_ticket_epc_id: number;
  departure_ticket_id: number;
  ticket_status: DepartureStatus;
  camera_person_episode_id: number;
  episode_start_utc: string | null;
  episode_end_utc: string | null;
  epc: string;
  item_id: number | null;
  sku: string | null;
  item_name: string | null;
  vendor_company_id: number | null;
  vendor_name: string | null;
  pre_read_count: number | null;
  post_miss_count: number | null;
  confidence: DepartureConfidence | null;
  confirmed_event_id: number | null;
  last_read_utc: string | null;
  confirmed_at: string | null;
  scu_name: string;
  smartbox_code: string | null;
  work_center: string | null;
  /** Departure/Arrival cancellation flag (migration 078). True when
   *  the owning customer has cancellation enabled AND there's a
   *  matching RE_ARRIVAL for the same (epc, scu) AND both events
   *  fall in the same cutoff-anchored week-window in the customer's
   *  timezone. The Arrivals row gets the same flag. */
  is_cancelled: boolean;
  /** The matching RE_ARRIVAL v2_tag_event.tag_event_id when paired.
   *  Set even when is_cancelled=false (pair exists but crossed the
   *  cutoff or customer hasn't enabled cancellation). */
  paired_event_id: number | null;
  paired_event_time: string | null;
  /** Price snapshot stamped on the ledger at CONFIRMED time (migration
   *  093). Independent of any later price changes — this is the
   *  amount the customer is billed for the line. NULL only when the
   *  live sync hasn't ingested this departure yet (next cycle catches
   *  it; usually <1 minute). 0 when the (vendor, customer, item)
   *  triple had no price configured at confirm time. */
  unit_price: number | null;
  currency: string | null;
  /** Customer's own part number for the vendor's item, snapshotted
   *  at CONFIRMED time. NULL when not configured. */
  customer_part_no: string | null;
  /** First AI-confirmed-person photo from the camera_person_episode
   *  this departure is paired with. Drives the row thumbnail; click
   *  opens the full episode in a modal. NULL when the episode has
   *  no person frames (rare). */
  thumbnail_url: string | null;
}

export interface DepartureEpcsListResponse {
  data: DepartureEpcRow[];
  pagination: { total: number; limit: number; offset: number };
}

export function listDepartureEpcs(
  token: string,
  q: {
    scu?: string; since?: string; until?: string; epc?: string;
    limit?: number; offset?: number;
  } = {},
): Promise<DepartureEpcsListResponse> {
  const params = new URLSearchParams();
  if (q.scu) params.set("scu", q.scu);
  if (q.since) params.set("since", q.since);
  if (q.until) params.set("until", q.until);
  if (q.epc) params.set("epc", q.epc);
  if (q.limit != null) params.set("limit", String(q.limit));
  if (q.offset != null) params.set("offset", String(q.offset));
  const qs = params.toString() ? `?${params.toString()}` : "";
  return req(token, `/api/departures/epcs${qs}`);
}
